/**
 * Refresh + Export button with hover-open dropdown menu.
 * Used by CompanyGHGPage, GHGOverviewPage, CompanyStatisticsPage, StatisticsOverviewPage.
 */

import React from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Download as DownloadIcon, Refresh } from '@mui/icons-material';

export interface ExportButtonMenuProps {
  /** Called when the refresh icon is clicked. */
  onRefresh: () => void;
  /** Called when "Export to Excel" is clicked. */
  onExcelExport: () => void;
  /** Called when "Export to PDF" is clicked. */
  onPdfExport: () => void;
  /** Whether an export is in progress (disables the button and shows a spinner). */
  exporting: boolean;
  /** When false the export button is hidden entirely (used by GHGOverviewPage). */
  showExport?: boolean;
}

const ExportButtonMenu: React.FC<ExportButtonMenuProps> = ({
  onRefresh,
  onExcelExport,
  onPdfExport,
  exporting,
  showExport = true,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Refresh data">
        <IconButton
          onClick={onRefresh}
          size="small"
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </Tooltip>
      {showExport && (
        <>
          <Button
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={exporting}
            onMouseEnter={handleMouseEnter}
            size="small"
          >
            Export
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            MenuListProps={{ onMouseLeave: handleClose }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleClose(); onExcelExport(); }}>
              Export to Excel
            </MenuItem>
            <MenuItem onClick={() => { handleClose(); onPdfExport(); }}>
              Export to PDF
            </MenuItem>
          </Menu>
        </>
      )}
    </>
  );
};

export default ExportButtonMenu;
